// EDV Next 0.11.4 - product shell primitives: Header, UserMenu, Breadcrumbs,
// Toaster, ConfirmDialog, Skeleton, EmptyState, ErrorState.
// All components are visually consistent via design.css tokens.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// ---------- Toaster ----------
export type ToastKind = 'info' | 'ok' | 'warn' | 'danger';
export interface Toast { id: number; kind: ToastKind; text: string; }
interface ToastCtx {
  toasts: Toast[];
  push(text: string, kind?: ToastKind): void;
  dismiss(id: number): void;
}
const ToastContext = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const push = useCallback((text: string, kind: ToastKind = 'info') => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const value = useMemo<ToastCtx>(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  );
}
function ToastViewport() {
  const ctx = useContext(ToastContext);
  if (!ctx || ctx.toasts.length === 0) return null;
  return (
    <div className="edv-toast-wrap" role="region" aria-label="Notifications">
      {ctx.toasts.map((t) => (
        <div key={t.id} className={`edv-toast ${t.kind}`} role="status" aria-live="polite" data-testid={`toast-${t.kind}`}>
          {t.text}
          <button
            aria-label="Dismiss notification"
            onClick={() => ctx.dismiss(t.id)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--edv-muted)' }}
          >×</button>
        </div>
      ))}
    </div>
  );
}
export function useToasts(): ToastCtx {
  const v = useContext(ToastContext);
  if (!v) throw new Error('ToastProvider missing');
  return v;
}

// ---------- ConfirmDialog ----------
export interface ConfirmOpts {
  title: string;
  body: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}
interface ConfirmCtx { confirm(opts: ConfirmOpts): Promise<boolean>; }
const ConfirmContext = createContext<ConfirmCtx | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState<ConfirmOpts | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);
  const confirm = useCallback((opts: ConfirmOpts) => {
    setOpen(opts);
    return new Promise<boolean>((res) => { resolveRef.current = res; });
  }, []);
  function finish(result: boolean) {
    setOpen(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  }
  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {open && (
        <div className="edv-dialog-back" role="dialog" aria-modal="true" aria-labelledby="cnf-title">
          <div className="edv-dialog">
            <header>
              <span id="cnf-title" className="title">{open.title}</span>
              <button aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={() => finish(false)}>×</button>
            </header>
            <div className="body">{open.body}</div>
            <div className="actions">
              <button className="btn" onClick={() => finish(false)}>{open.cancelText ?? 'Cancel'}</button>
              <button className={`btn ${open.destructive ? 'danger' : 'primary'}`} onClick={() => finish(true)} data-testid="confirm-ok">
                {open.confirmText ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
export function useConfirm() {
  const v = useContext(ConfirmContext);
  if (!v) throw new Error('ConfirmProvider missing');
  return v.confirm;
}

// ---------- User menu ----------
export function UserMenu({ email, onSignOut }: { email: string; onSignOut(): void }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);
  return (
    <div className="edv-user-menu" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} data-testid="user-menu">
        {email} ▾
      </button>
      {open && (
        <div className="menu" role="menu">
          <button role="menuitem" onClick={() => { setOpen(false); onSignOut(); }} data-testid="user-signout">Sign out</button>
        </div>
      )}
    </div>
  );
}

// ---------- Breadcrumbs ----------
export function Breadcrumbs({ items }: { items: Array<{ label: string; testid?: string }> }) {
  return (
    <nav className="edv-crumbs" aria-label="Breadcrumb">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden>›</span>}
          {i === items.length - 1 ? (
            <strong data-testid={it.testid}>{it.label}</strong>
          ) : (
            <span data-testid={it.testid}>{it.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

// ---------- Empty + Error + Skeleton ----------
export function EmptyState({ title, hint, action, testid }: { title: string; hint?: string; action?: React.ReactNode; testid?: string }) {
  return (
    <div className="edv-empty" data-testid={testid ?? 'empty-state'}>
      <div className="title">{title}</div>
      {hint && <div className="small">{hint}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div className="edv-error" role="alert">
      Something went wrong. <span className="small mono">{msg}</span>
      {onRetry && <button className="btn sm" style={{ marginLeft: 8 }} onClick={onRetry}>Retry</button>}
    </div>
  );
}
export function Skeleton({ lines = 3, wide }: { lines?: number; wide?: boolean }) {
  return (
    <div role="status" aria-live="polite" aria-label="Loading" data-testid="skeleton">
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className={`edv-skeleton ${wide ? 'wide' : ''}`} style={{ width: `${100 - i * 8}%` }} />
      ))}
    </div>
  );
}
